/**
 * backgroundProps.js
 * ──────────────────
 * Factory for generating a standard set of Vue 2 background prop definitions.
 *
 * The complete "background control" in the CMS sidebar exposes these fields:
 *   backgroundColor, backgroundImage, backgroundSize, backgroundPosition,
 *   backgroundRepeat, backgroundOverlay, backgroundOverlayOpacity
 *
 * Use `createBackgroundProps()` when defining block props to ensure every
 * block uses consistent prop names and defaults.  Pair it with
 * `buildBackgroundStyle()` from `blockUtils.js` to compute the style object.
 *
 * DB-COMPATIBILITY: The generated prop names become JSON keys stored in the
 * database.  Never rename a key without a migration strategy — just add new
 * ones alongside the old ones instead.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Usage — single background (no prefix):
 *
 *   import { createBackgroundProps } from '@blocks/shared/backgroundProps.js';
 *
 *   props: Object.assign({}, createBackgroundProps(), {
 *     text: { type: String, default: '' }
 *   })
 *
 * Usage — block that has multiple background areas (prefixed):
 *
 *   import { createBackgroundProps } from '@blocks/shared/backgroundProps.js';
 *
 *   props: Object.assign(
 *     {},
 *     createBackgroundProps('section',  { backgroundColor: '#1E2031' }),
 *     createBackgroundProps('card',     { backgroundColor: '#FFFFFF' }),
 *     { otherProp: { type: String, default: '' } }
 *   )
 *
 * In the above, 'section' generates: sectionBackgroundColor, sectionBackgroundImage, …
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * The canonical list of background field base-names (without prefix).
 * These are the keys recognised by `buildBackgroundStyle()` in blockUtils.js.
 */
export var BACKGROUND_FIELD_NAMES = [
	'backgroundColor',
	'backgroundImage',
	'backgroundSize',
	'backgroundPosition',
	'backgroundRepeat',
	'backgroundOverlay',
	'backgroundOverlayOpacity'
];

import { capitalize } from './blockUtils.js';

/**
 * Generate a Vue 2 props definition fragment for the standard background fields.
 *
 * @param {string}  [prefix='']   Optional camelCase prefix applied to every
 *                                prop name.  e.g. 'section' → sectionBackgroundColor.
 *                                When omitted (or '') props are unprefixed.
 * @param {object}  [defaults={}] Default value overrides keyed by the BASE
 *                                field name (without prefix), e.g. { backgroundColor: '#FFF' }.
 * @returns {object} Vue props definition fragment ready to be spread into
 *                   a component's `props` option.
 */
export function createBackgroundProps(prefix, defaults) {
	// camelCase: 'dropdown' → dropdownBackgroundColor (the prefix itself stays lower-case)
	var p = prefix || '';
	var d = defaults || {};

	var result = {};
	var bgColor    = p ? p + 'BackgroundColor'          : 'backgroundColor';
	var bgImage    = p ? p + 'BackgroundImage'          : 'backgroundImage';
	var bgSize     = p ? p + 'BackgroundSize'           : 'backgroundSize';
	var bgPos      = p ? p + 'BackgroundPosition'       : 'backgroundPosition';
	var bgRepeat   = p ? p + 'BackgroundRepeat'         : 'backgroundRepeat';
	var bgOverlay  = p ? p + 'BackgroundOverlay'        : 'backgroundOverlay';
	var bgOverOp   = p ? p + 'BackgroundOverlayOpacity' : 'backgroundOverlayOpacity';

	result[bgColor]    = { type: String, default: d.backgroundColor          !== undefined ? d.backgroundColor          : '' };
	result[bgImage]    = { type: String, default: d.backgroundImage          !== undefined ? d.backgroundImage          : '' };
	result[bgSize]     = { type: String, default: d.backgroundSize           !== undefined ? d.backgroundSize           : 'cover' };
	result[bgPos]      = { type: String, default: d.backgroundPosition       !== undefined ? d.backgroundPosition       : 'center center' };
	result[bgRepeat]   = { type: String, default: d.backgroundRepeat         !== undefined ? d.backgroundRepeat         : 'no-repeat' };
	result[bgOverlay]  = { type: String, default: d.backgroundOverlay        !== undefined ? d.backgroundOverlay        : '' };
	result[bgOverOp]   = { type: String, default: d.backgroundOverlayOpacity !== undefined ? d.backgroundOverlayOpacity : '0.5' };

	return result;
}

/**
 * Extract the resolved background values from a flat props map,
 * handling both prefixed and non-prefixed keys.
 *
 * @param {object} props   Flat prop values (typically from v-bind or block.props)
 * @param {string} [prefix=''] Optional prefix used when creating the props.
 * @returns {object} Normalised values keyed by base field name.
 */
export function extractBackgroundValues(props, prefix) {
	// camelCase: 'dropdown' → dropdownBackgroundColor (the prefix itself stays lower-case)
	var p = prefix || '';
	var result = {};
	for (var i = 0; i < BACKGROUND_FIELD_NAMES.length; i++) {
		var baseName = BACKGROUND_FIELD_NAMES[i];
		var propName = p + (p ? capitalize(baseName) : baseName);
		result[baseName] = props[propName] !== undefined ? props[propName] : '';
	}
	return result;
}

